import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';

// ─── CONSTANTS ─────────────────────────────────────────────────────────────────

const PREF_STEPS = 6;

const INTENTION_OPTS = [
  { label: 'Life partner', value: 'marriage' },
  { label: 'Long term only', value: 'long_term' },
  { label: 'Short or long term', value: 'open' },
  { label: 'Short term only', value: 'short_term' },
];

const INCOME_OPTS = [
  { label: '$50K+',  value: 50000 },
  { label: '$100K+', value: 100000 },
  { label: '$200K+', value: 200000 },
  { label: '$500K+', value: 500000 },
];

const EARNING_OPTS = [
  { label: 'I want to be the primary earner', value: 'i_primary' },
  { label: 'Equal earners', value: 'equal' },
  { label: 'I want my partner to earn more', value: 'partner_primary' },
  { label: 'No preference', value: 'no_preference' },
];

const EXCLUSIVITY_OPTS = [
  { label: 'Monogamy only — exclusive committed partnership', value: 'monogamy_only' },
  { label: 'Monogamy open to evolving — start exclusive, open to discussion later', value: 'monogamy_open_to_evolving' },
  { label: 'Non-monogamy — open, poly, or other ENM structures', value: 'non_monogamy_required' },
  { label: 'Open to discussing — depends on the connection', value: 'open_to_discussing' },
];

const HEIGHT_OPTS = [];
for (let i = 56; i <= 84; i++) {
  HEIGHT_OPTS.push({ label: `${Math.floor(i / 12)}'${i % 12}"`, value: i });
}

// ─── DUAL RANGE SLIDER ────────────────────────────────────────────────────────

function DualRangeSlider({ min, max, onChange, lo = 18, hi = 65 }) {
  const pct = v => ((v - lo) / (hi - lo)) * 100;
  return (
    <div style={{ position: 'relative', height: 40, display: 'flex', alignItems: 'center' }}>
      <div style={{
        position: 'absolute', left: 0, right: 0, height: 3,
        background: '#2a2a2a', borderRadius: 3,
      }} />
      <div style={{
        position: 'absolute',
        left: `${pct(min)}%`,
        width: `${pct(max) - pct(min)}%`,
        height: 3,
        background: 'var(--gold)',
        borderRadius: 3,
        pointerEvents: 'none',
      }} />
      <input
        type="range" min={lo} max={hi} value={min}
        onChange={e => onChange(Math.min(parseInt(e.target.value), max - 1), max)}
        className="dual-range-thumb"
        style={{ zIndex: min > hi - 5 ? 5 : 3 }}
      />
      <input
        type="range" min={lo} max={hi} value={max}
        onChange={e => onChange(min, Math.max(parseInt(e.target.value), min + 1))}
        className="dual-range-thumb"
        style={{ zIndex: 4 }}
      />
    </div>
  );
}

// ─── SHARED COMPONENTS ─────────────────────────────────────────────────────────

function StepHeader({ step, title, onBack }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 0, color: 'var(--gold)', fontSize: 20, lineHeight: 1,
          }}
        >
          ←
        </button>
        <div className="step-indicator" style={{ margin: 0 }}>
          Step {step} of {PREF_STEPS}
        </div>
      </div>
      <div className="display-md">{title}</div>
    </div>
  );
}

function RadioCards({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`option-card${value === opt.value ? ' selected' : ''}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function PreCards({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`option-card${value === opt.value ? ' selected' : ''}`}
          style={{ flex: 1, minWidth: 90, textAlign: 'center', fontSize: 13, padding: '12px 10px' }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function Gated({ show, children }) {
  return (
    <div style={{
      overflow: 'hidden',
      maxHeight: show ? 1000 : 0,
      opacity: show ? 1 : 0,
      transition: 'max-height 300ms ease, opacity 250ms ease',
    }}>
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ marginTop: 28, marginBottom: 14 }}>
      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 400, opacity: 0.75 }}>
        {children}
      </div>
    </div>
  );
}

function PreQuestion({ children }) {
  return (
    <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', marginBottom: 16, lineHeight: 1.6, fontWeight: 300 }}>
      {children}
    </div>
  );
}

// ─── STEP 1 — Who you're open to ───────────────────────────────────────────────

function Step1({ form, set }) {
  return (
    <div>
      <SectionLabel>Age range</SectionLabel>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>{form.pref_age_min}</span>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>{form.pref_age_max}</span>
      </div>
      <DualRangeSlider
        min={form.pref_age_min}
        max={form.pref_age_max}
        onChange={(min, max) => { set('pref_age_min', min); set('pref_age_max', max); }}
      />

      <SectionLabel>Distance</SectionLabel>
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>
          {form.pref_relocation_open ? 'Anywhere' : `${form.pref_distance_miles} miles`}
        </span>
      </div>
      <input type="range" min={5} max={200} value={form.pref_distance_miles}
        onChange={e => set('pref_distance_miles', e.target.value)}
        disabled={form.pref_relocation_open}
        style={{ width: '100%', accentColor: 'var(--gold)' }} />
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 10 }}>
        <input type="checkbox" checked={form.pref_relocation_open}
          onChange={e => set('pref_relocation_open', e.target.checked)}
          style={{ accentColor: 'var(--gold)', width: 16, height: 16 }} />
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>Open to relocating</span>
      </label>

      <SectionLabel>What are you looking for?</SectionLabel>
      <RadioCards options={INTENTION_OPTS} value={form.pref_relationship_intention}
        onChange={v => set('pref_relationship_intention', v)} />
    </div>
  );
}

// ─── STEP 2 — Lifestyle ────────────────────────────────────────────────────────

function Step2({ form, set }) {
  return (
    <div>
      <SectionLabel>Smoking</SectionLabel>
      <RadioCards
        options={[
          { label: 'Non-smokers only', value: 'never' },
          { label: 'Occasional is fine', value: 'occasional_ok' },
          { label: 'No preference', value: 'no_preference' },
        ]}
        value={form.pref_smoking}
        onChange={v => set('pref_smoking', v)}
      />

      <SectionLabel>Substances</SectionLabel>
      <RadioCards
        options={[
          { label: 'Never', value: 'never' },
          { label: 'Occasional use is fine', value: 'occasional_ok' },
          { label: 'No preference', value: 'no_preference' },
        ]}
        value={form.pref_drugs}
        onChange={v => set('pref_drugs', v)}
      />
    </div>
  );
}

// ─── STEP 3 — Family ──────────────────────────────────────────────────────────

function Step3({ form, set }) {
  const showHasChildren = form.pref_wants_children === 'must_want' || form.pref_wants_children === 'open';

  return (
    <div>
      <SectionLabel>Children</SectionLabel>
      <RadioCards
        options={[
          { label: 'Must want children', value: 'must_want' },
          { label: 'Must not want children', value: 'must_not_want' },
          { label: 'Open either way', value: 'open' },
        ]}
        value={form.pref_wants_children}
        onChange={v => {
          set('pref_wants_children', v);
          if (v === 'must_not_want') set('pref_has_children', '');
        }}
      />

      <Gated show={showHasChildren}>
        <SectionLabel>Do they already have children?</SectionLabel>
        <RadioCards
          options={[
            { label: 'No children only', value: 'no_kids_only' },
            { label: 'Having children is fine', value: 'has_kids_ok' },
            { label: 'No preference', value: 'no_preference' },
          ]}
          value={form.pref_has_children}
          onChange={v => set('pref_has_children', v)}
        />
      </Gated>
    </div>
  );
}

// ─── STEP 4 — Money & ambition ────────────────────────────────────────────────

function Step4({ form, set }) {
  const gate = form.finance_gate;
  const showIncome  = gate === 'yes';
  const showEarning = gate === 'yes' || gate === 'somewhat';

  return (
    <div>
      <PreQuestion>
        Does financial alignment matter to you in a partner?
      </PreQuestion>
      <PreCards
        options={[
          { label: 'Yes, significantly', value: 'yes' },
          { label: 'Somewhat', value: 'somewhat' },
          { label: 'Not really', value: 'no' },
        ]}
        value={form.finance_gate}
        onChange={v => set('finance_gate', v)}
      />

      <Gated show={showIncome}>
        <SectionLabel>Income floor</SectionLabel>
        <RadioCards options={INCOME_OPTS} value={form.pref_income_min}
          onChange={v => set('pref_income_min', v)} />
        <div style={{ fontSize: 11, color: 'var(--very-muted)', marginTop: 8, fontStyle: 'italic', lineHeight: 1.6 }}>
          We also factor net worth — someone retired with assets passes this.
        </div>
      </Gated>

      <Gated show={showEarning}>
        <SectionLabel>Earning dynamic</SectionLabel>
        <RadioCards options={EARNING_OPTS} value={form.pref_earning_dynamic}
          onChange={v => set('pref_earning_dynamic', v)} />
      </Gated>
    </div>
  );
}

// ─── STEP 5 — Physical preferences ───────────────────────────────────────────

function Step5({ form, set }) {
  const showHeight = form.height_gate === 'yes';

  return (
    <div>
      <PreQuestion>
        Want to set height preferences?
      </PreQuestion>
      <PreCards
        options={[
          { label: 'Yes, set them', value: 'yes' },
          { label: 'Skip — open to anyone', value: 'skip' },
        ]}
        value={form.height_gate}
        onChange={v => set('height_gate', v)}
      />

      <Gated show={showHeight}>
        <SectionLabel>Height range</SectionLabel>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 4 }}>
          <div className="field-group" style={{ flex: 1, marginBottom: 0 }}>
            <label className="field-label">Min height</label>
            <select className="field" value={form.pref_height_min_inches || ''}
              onChange={e => set('pref_height_min_inches', e.target.value || null)}>
              <option value="">No minimum</option>
              {HEIGHT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={{ color: 'var(--very-muted)', paddingTop: 20 }}>—</div>
          <div className="field-group" style={{ flex: 1, marginBottom: 0 }}>
            <label className="field-label">Max height</label>
            <select className="field" value={form.pref_height_max_inches || ''}
              onChange={e => set('pref_height_max_inches', e.target.value || null)}>
              <option value="">No maximum</option>
              {HEIGHT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </Gated>
    </div>
  );
}

// ─── STEP 6 — Relationship structure & intimacy ───────────────────────────────

function Step6({ form, set }) {
  const showIntimacyDetails = form.pref_intimacy_gateway === 'alternative';

  return (
    <div>
      <SectionLabel>Relationship structure</SectionLabel>
      <RadioCards options={EXCLUSIVITY_OPTS} value={form.pref_exclusivity}
        onChange={v => set('pref_exclusivity', v)} />

      <SectionLabel>Physical intimacy</SectionLabel>
      <PreQuestion>
        When it comes to physical intimacy with a partner...
      </PreQuestion>
      <PreCards
        options={[
          { label: "I'm fairly private — I know what I like", value: 'traditional' },
          { label: "I'm open to sharing — compatibility matters here", value: 'alternative' },
        ]}
        value={form.pref_intimacy_gateway}
        onChange={v => {
          set('pref_intimacy_gateway', v);
          if (v === 'traditional') {
            set('pref_intimacy_energy', '');
            set('pref_intimacy_drive', '');
            set('pref_intimacy_kink', '');
          }
        }}
      />

      <Gated show={showIntimacyDetails}>
        <SectionLabel>Energy dynamic</SectionLabel>
        <RadioCards
          options={[
            { label: 'A more dominant partner', value: 'dominant' },
            { label: 'A more submissive partner', value: 'submissive' },
            { label: 'A switch — fluid roles', value: 'switch' },
            { label: 'Equal energy', value: 'equal' },
            { label: 'No strong preference', value: 'no_preference' },
          ]}
          value={form.pref_intimacy_energy}
          onChange={v => set('pref_intimacy_energy', v)}
        />

        <SectionLabel>Drive level</SectionLabel>
        <RadioCards
          options={[
            { label: 'High drive — physical connection is central', value: 'high' },
            { label: 'Moderate drive — meaningful but not constant', value: 'moderate' },
            { label: 'Open to flexibility', value: 'variable' },
            { label: 'No strong preference', value: 'no_preference' },
          ]}
          value={form.pref_intimacy_drive}
          onChange={v => set('pref_intimacy_drive', v)}
        />

        <SectionLabel>How explorative are you in the bedroom?</SectionLabel>
        <RadioCards
          options={[
            { label: 'Fun is fun — no shame here', value: 'high_ok' },
            { label: "I'm open to trying new things", value: 'moderate_ok' },
            { label: "Let's keep it simple and connective", value: 'light_only' },
            { label: 'No strong preference', value: 'no_preference' },
          ]}
          value={form.pref_intimacy_kink}
          onChange={v => set('pref_intimacy_kink', v)}
        />
      </Gated>
    </div>
  );
}

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────

const STEP_TITLES = [
  "Who you're open to",
  'Lifestyle',
  'Family',
  'Money & ambition',
  'Physical preferences',
  'Relationship structure',
];

const STEPS = [Step1, Step2, Step3, Step4, Step5, Step6];

export default function Preferences() {
  const navigate  = useNavigate();
  const [step, setStep]       = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [form, setForm] = useState({
    // Step 1
    pref_age_min:              21,
    pref_age_max:              65,
    pref_distance_miles:       50,
    pref_relocation_open:      false,
    pref_relationship_intention: '',
    // Step 2
    pref_smoking:  '',
    pref_drugs:    '',
    // Step 3
    pref_wants_children: '',
    pref_has_children:   '',
    // Step 4 — finance gate defaults to 'somewhat' so users can tap Continue immediately
    finance_gate:          'somewhat',
    pref_income_min:       null,
    pref_earning_dynamic:  '',
    // Step 5
    height_gate:             '',
    pref_height_min_inches:  null,
    pref_height_max_inches:  null,
    // Step 6
    pref_exclusivity:       '',
    pref_intimacy_gateway:  '',
    pref_intimacy_energy:   '',
    pref_intimacy_drive:    '',
    pref_intimacy_kink:     '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Silent warmup ping on mount — bypasses the retry/waking-toast loop
  useEffect(() => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    fetch(`${base}/`, { mode: 'no-cors' }).catch(() => {});
  }, []);

  const goBack = () => {
    if (step === 0) navigate(-1);
    else setStep(s => s - 1);
  };

  const canContinue = () => {
    if (step === 4) return !!form.height_gate;            // Step 5: must pick yes/skip
    if (step === 5) return !!form.pref_exclusivity && !!form.pref_intimacy_gateway; // Step 6: required
    return true;
  };

  const buildPayload = () => {
    const f = form;
    const payload = {
      pref_age_min:                parseInt(f.pref_age_min)  || 21,
      pref_age_max:                parseInt(f.pref_age_max)  || 65,
      pref_distance_miles:         parseInt(f.pref_distance_miles) || 50,
      pref_relocation_open:        f.pref_relocation_open,
      pref_relationship_intention: f.pref_relationship_intention || null,
      pref_smoking:                f.pref_smoking  || null,
      pref_drugs:                  f.pref_drugs    || null,
      pref_wants_children:         f.pref_wants_children || null,
      // pref_has_children is irrelevant when user must_not_want children
      pref_has_children: f.pref_wants_children === 'must_not_want'
        ? null
        : (f.pref_has_children || null),
      pref_exclusivity:       f.pref_exclusivity      || null,
      pref_intimacy_gateway:  f.pref_intimacy_gateway || null,
    };

    // Finance gate
    if (f.finance_gate === 'no') {
      payload.pref_income_min      = null;
      payload.pref_earning_dynamic = 'no_preference';
    } else if (f.finance_gate === 'somewhat') {
      payload.pref_income_min      = null;
      payload.pref_earning_dynamic = f.pref_earning_dynamic || null;
    } else {
      payload.pref_income_min      = f.pref_income_min || null;
      payload.pref_earning_dynamic = f.pref_earning_dynamic || null;
    }

    // Height gate
    if (f.height_gate === 'skip') {
      payload.pref_height_min_inches = null;
      payload.pref_height_max_inches = null;
    } else {
      payload.pref_height_min_inches = f.pref_height_min_inches ? parseInt(f.pref_height_min_inches) : null;
      payload.pref_height_max_inches = f.pref_height_max_inches ? parseInt(f.pref_height_max_inches) : null;
    }

    // Intimacy gate — traditional path submits no_preference for all sub-fields
    if (f.pref_intimacy_gateway === 'alternative') {
      payload.pref_intimacy_energy = f.pref_intimacy_energy || 'no_preference';
      payload.pref_intimacy_drive  = f.pref_intimacy_drive  || 'no_preference';
      payload.pref_intimacy_kink   = f.pref_intimacy_kink   || 'no_preference';
    } else {
      payload.pref_intimacy_energy = 'no_preference';
      payload.pref_intimacy_drive  = 'no_preference';
      payload.pref_intimacy_kink   = 'no_preference';
    }

    return payload;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/profile/preferences', buildPayload());
      navigate('/onboarding/path');
    } catch (err) {
      setError(err.message || 'Something went wrong — please try again.');
      setLoading(false);
    }
  };

  const handleContinue = () => {
    // Save whatever we have so far — wakes Render early and keeps it warm
    api.post('/profile/preferences', buildPayload()).catch(() => {});
    setStep(s => s + 1);
  };

  const isLastStep  = step === PREF_STEPS - 1;
  const CurrentStep = STEPS[step];

  return (
    <div className="auth-screen" style={{ paddingTop: 24, paddingBottom: 100 }}>
      <StepHeader step={step + 1} title={STEP_TITLES[step]} onBack={goBack} />
      <CurrentStep form={form} set={set} />
      {error && (
        <div style={{ marginTop: 20, fontSize: 13, color: '#e05c5c', textAlign: 'center', lineHeight: 1.5 }}>
          {error}
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <button
          className="btn btn-gold"
          onClick={isLastStep ? handleSubmit : handleContinue}
          disabled={loading || !canContinue()}
        >
          {loading ? <span className="spinner" /> : isLastStep ? 'Finish' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}
